import { MetricBackendService } from "./metricBackendService";
import { MetricQueryBackendService } from "./metricQueryBackendService";

// Declare angular as a global variable for TypeScript
declare const angular: any;

/**
 * React/TypeScript MetricBackend Module
 *
 * This module provides a complete React/TypeScript implementation of the metric backend services
 * with event-driven architecture similar to the original Angular module.
 */

interface EventService {
  register(
    namespace: string
  ): {
    subscribe(eventName: string, handler: Function): void;
    publish(eventName: string, ...args: any[]): void;
  };
}

interface AppConfig {
  features: {
    queryService?: boolean;
    migratedMetricBackendService?: boolean;
    migratedMetricQueryBackendService?: boolean;
    migratedMetricBackendModule?: boolean;
    [key: string]: any;
  };
}

interface MetricBackendDependencies {
  AppConfig: AppConfig;
  EventService: EventService;
  backendApi: any;
  ApiPath: any;
  Metric: any;
  MetricSparkline: any;
  MetricSummary: any;
  QueryClient?: any;
  QueryGenerator?: any;
  QueryErrorInterpreter?: any;
  MetricDataAdapter?: any;
  TableDataAdapter?: any;
  DataFilter?: any;
  TableAdapter?: any;
}

/**
 * React/TypeScript MetricBackend Module
 */
export class MetricBackendModule {
  // Timeout in milliseconds to wait before retrying Angular readiness
  private static readonly ANGULAR_READY_RETRY_TIMEOUT_MS = 100;

  private metricBackendService: MetricBackendService;
  private metricQueryBackendService: MetricQueryBackendService;
  private eventService: any;
  private appConfig: AppConfig;

  constructor(dependencies: MetricBackendDependencies) {
    this.appConfig = dependencies.AppConfig;
    this.eventService = dependencies.EventService.register("acl.visualizer.metric.backend");

    // Initialize services
    this.metricBackendService = new MetricBackendService({
      backendApi: dependencies.backendApi,
      ApiPath: dependencies.ApiPath,
      Metric: dependencies.Metric,
      MetricSparkline: dependencies.MetricSparkline,
      MetricSummary: dependencies.MetricSummary,
    });

    this.metricQueryBackendService = new MetricQueryBackendService({
      QueryClient: dependencies.QueryClient,
      QueryGenerator: dependencies.QueryGenerator,
      QueryErrorInterpreter: dependencies.QueryErrorInterpreter,
      MetricDataAdapter: dependencies.MetricDataAdapter,
      TableDataAdapter: dependencies.TableDataAdapter,
      DataFilter: dependencies.DataFilter,
      TableAdapter: dependencies.TableAdapter,
      MetricSparkline: dependencies.MetricSparkline,
      backendApi: dependencies.backendApi,
      ApiPath: dependencies.ApiPath,
      Metric: dependencies.Metric,
      MetricSummary: dependencies.MetricSummary,
    });

    this.setupEventHandlers();
    this.overrideAngularServices();
  }

  private setupEventHandlers(): void {
    const dataRequestMapping: { [key: string]: Function } = {
      metricRequest: this.metricBackendService.metricRequested.bind(this.metricBackendService),
      metricCreate: this.metricBackendService.metricCreated.bind(this.metricBackendService),
      metricChange: this.metricBackendService.metricChanged.bind(this.metricBackendService),
      metricDelete: this.metricBackendService.metricDeleted.bind(this.metricBackendService),
      metricsRequest: this.metricBackendService.metricsRequested.bind(this.metricBackendService),
      metricSummaryRequest: this.metricBackendService.metricSummaryRequested.bind(this.metricBackendService),
      metricSparklineRequest: this.metricBackendService.metricSparklineRequested.bind(this.metricBackendService),
    };

    const eventMappingToQuery: { [key: string]: Function } = {
      metricSparklineRequest: this.metricQueryBackendService.getMetricSparklineData.bind(
        this.metricQueryBackendService
      ),
    };

    for (const eventName in dataRequestMapping) {
      let requestFunction: Function;

      if (this.appConfig.features.queryService && eventMappingToQuery[eventName]) {
        requestFunction = eventMappingToQuery[eventName];
      } else {
        requestFunction = dataRequestMapping[eventName];
      }

      this.eventService.subscribe(`request.${eventName}`, this.delegateDataRequest(eventName, requestFunction));
    }
  }

  private delegateDataRequest(eventName: string, requestFunction: Function) {
    return (...args: any[]) => {
      const token = args[1];
      const requestArgs = args.slice(2);

      requestFunction(...requestArgs)
        .then((data: any) => {
          this.eventService.publish(`resolve.${eventName}`, token, data);
        })
        .catch((error: any) => {
          this.eventService.publish(`reject.${eventName}`, token, error);
        });
    };
  }

  /**
   * Make React services globally available for Angular to use
   * This allows Angular code to access React services when needed
   */
  private makeServicesGloballyAvailable(): void {
    try {
      // Make React services available on window object for Angular to access
      if (typeof window !== "undefined") {
        (window as any).ReactMetricBackendServices = {
          MetricBackendService: this.metricBackendService,
          MetricQueryBackendService: this.metricQueryBackendService,
          isReactImplementation: true,
        };
      }
    } catch (error) {
      console.warn("⚠️ Failed to make React services globally available:", error);
    }
  }

  /**
   * Override Angular event handling completely when React module is enabled
   * This approach hijacks Angular's EventService to redirect events to React
   */
  private overrideAngularServices(): void {
    try {
      // Wait for Angular to be ready
      if (typeof angular === "undefined") {
        setTimeout(() => this.overrideAngularServices(), MetricBackendModule.ANGULAR_READY_RETRY_TIMEOUT_MS);
        return;
      }

      const injector = angular.element(document.body).injector();
      if (!injector) {
        setTimeout(() => this.overrideAngularServices(), MetricBackendModule.ANGULAR_READY_RETRY_TIMEOUT_MS);
        return;
      }

      // If the complete module migration is enabled, take over all event handling
      if (this.appConfig.features.migratedMetricBackendModule) {
        this.hijackAngularEventService(injector);
      } else {
        // Individual service migration - just make services available
        this.makeServicesGloballyAvailable();
      }
    } catch (error) {
      console.warn("⚠️ Failed to override Angular services:", error);
    }
  }

  /**
   * Hijack Angular's EventService to redirect metric backend events to React
   */
  private hijackAngularEventService(injector: any): void {
    try {
      const EventService = injector.get("EventService");
      const originalRegister = EventService.register;

      // Override EventService.register for metric backend namespace
      EventService.register = function(namespace: string) {
        if (namespace === "acl.visualizer.metric.backend") {
          // Return a proxy that redirects to React event handling
          return {
            subscribe: function(eventName: string, handler: Function) {
              // Angular events will be handled by React module's event system
            },
            publish: function(eventName: string, ...args: any[]) {
              // Events are published by React services directly
            },
          };
        } else {
          // For other namespaces, use original EventService
          return originalRegister.call(this, namespace);
        }
      };
    } catch (error) {
      console.warn("⚠️ Failed to hijack Angular EventService:", error);
    }
  }

  /**
   * Get the MetricBackendService instance
   */
  public getMetricBackendService(): MetricBackendService {
    return this.metricBackendService;
  }

  /**
   * Get the MetricQueryBackendService instance
   */
  public getMetricQueryBackendService(): MetricQueryBackendService {
    return this.metricQueryBackendService;
  }

  /**
   * Static factory method to create and initialize the module
   */
  static initialize(dependencies: MetricBackendDependencies): MetricBackendModule {
    return new MetricBackendModule(dependencies);
  }
}

/**
 * Angular bridge function for backward compatibility
 * This allows the React module to be used in Angular applications
 */
export function createAngularBridge(dependencies: MetricBackendDependencies) {
  const module = MetricBackendModule.initialize(dependencies);

  return {
    MetricBackendService: module.getMetricBackendService(),
    MetricQueryBackendService: module.getMetricQueryBackendService(),
    module: module,
  };
}
